/**
 * Host payout onboarding — bank account form.
 *
 * Uses the shared payout form so personal and organization bank flows keep
 * identical validation, masking, haptics, and interaction behavior.
 *
 * Two things this screen deliberately does NOT do any more.
 *
 * It does not let someone reach the bank form without a verified identity.
 * There was no gate at all: a host could register where to send money having
 * proven nothing about who they are. That is a compliance problem on its own,
 * and it is also the reason the form had to ask for a name and a document —
 * with no verified identity behind it, asking was the only option available.
 *
 * And it does not ask for the DUI or the holder's name. Both are already
 * proven: Verifik checked the number against the national registry and the
 * name is the one the registry returned. The form shows them and moves on.
 * What is left is genuinely new information — which bank, which account,
 * what type.
 */
import { useCallback } from 'react'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { notification, NotificationType } from '@/lib/utils/haptics'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { EmptyState } from '@/components/ui/EmptyState'
import { PayoutAccountForm } from '@/components/hosting/PayoutAccountForm'
import { useCreatePayoutAccount } from '@/lib/hooks/use-payouts'
import { useVerifiedIdentity } from '@/lib/hooks/use-identity'
import { useAuth } from '@/lib/hooks/use-auth'
import { fullProfileName } from '@/lib/utils/profile-display'
import { SPACING } from '@/lib/theme/tokens'
import { getSvBankLabel } from '@/lib/data/sv-banks'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import type { PayoutAccountFormValues } from '@/lib/validations/payout.schema'

export default function PayoutOnboardingScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const createAccount = useCreatePayoutAccount()
  const { data: identity, isLoading } = useVerifiedIdentity()
  const { profile } = useAuth()

  // The verified document's name wins, because it is the one the registry
  // returned; the profile name is the fallback for the DUI-lookup path, which
  // approves from the number and the date of birth and never writes a name.
  const holderName = identity?.fullName || fullProfileName(profile)

  const handleSubmit = useCallback(
    async (values: PayoutAccountFormValues) => {
      try {
        await createAccount.mutateAsync({
          accountType: values.accountType,
          bankName: getSvBankLabel(values.bankCode),
          bankCode: values.bankCode,
          accountNumber: values.accountNumber,
          accountHolderName: values.accountHolderName,
          documentNumber: values.documentNumber,
          countryCode: 'SV',
        })
        void notification(NotificationType.Success)
        toast.success(t('payouts.onboarding_success'))
        router.replace('/hosting/payouts' as never)
      } catch (err) {
        void notification(NotificationType.Error)
        toast.error(errorMessage(err, t('payouts.onboarding_failed')))
        captureError(err, { screen: 'payouts/onboarding', action: 'create_account' })
      }
    },
    [createAccount, router, t],
  )

  // A verified identity with a number is the precondition. `documentNumber` and
  // not merely "approved": paying somebody we cannot name is what this gate
  // exists to prevent.
  const canReceivePayouts = Boolean(identity?.documentNumber)

  // Whether the DUI field may be shown as CONFIRMED is a narrower question than
  // whether the person is verified.
  //
  // The personal payout schema validates a Salvadoran DUI and nothing else. A
  // host verified with a passport or a foreign document has a real, proven
  // number that will never satisfy it — and locking that value into a read-only
  // field made the form permanently unsubmittable with no way to correct it.
  // Confirm it only when it is the document the form can actually accept;
  // otherwise the field stays editable so the person can supply their DUI.
  const documentIsSvDui =
    identity?.documentType === 'dui' && (identity?.issuingCountry ?? 'SV') === 'SV'

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader
        title={t('payouts.onboarding_title')}
        subtitle={t('payouts.onboarding_subtitle')}
        backTestID="payouts.onboarding-back"
      />

      <KeyboardAwareView
        includeTopInset={false}
        contentContainerStyle={{
          paddingHorizontal: SPACING.lg,
          paddingBottom: SPACING['3xl'],
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {isLoading ? null : canReceivePayouts ? (
          <PayoutAccountForm
            mode="personal"
            submitting={createAccount.isPending}
            submitLabel={t('payouts.onboarding_submit')}
            testIDPrefix="payouts"
            onSubmit={handleSubmit}
            initialHolderName={holderName}
            initialDocumentNumber={documentIsSvDui ? (identity?.documentNumber ?? '') : ''}
            identityProvenance={documentIsSvDui ? t('payouts.identity_source') : undefined}
            // The NAME only sometimes is. The official DUI-lookup path approves
            // from the number and the date of birth and never writes a name, so
            // for those hosts this falls back to the profile — which is
            // self-typed, and must not be labelled "De tu DUI verificado" nor
            // locked. It stays a prefilled, editable input, which still spares
            // the typing without claiming something we cannot claim.
            holderNameProvenance={identity?.fullName ? t('payouts.identity_source') : undefined}
          />
        ) : (
          <EmptyState
            title={t('payouts.identity_required_title')}
            description={t('payouts.identity_required_body')}
            action={{
              label: t('payouts.identity_required_action'),
              onPress: () => router.push('/profile/verification/identity' as never),
            }}
          />
        )}
      </KeyboardAwareView>
    </ScreenContainer>
  )
}
